import React, { useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import Spinner from "../../ui/spinner";
import { DateTime } from "luxon";
import { Maximize2Icon, Minimize2Icon } from "lucide-react";
import { useProject } from "../../../data/project";
import { useProjectProgressQuery } from "../../../data/queries/project-progress";
import { useApplyTimelinesMutation } from "../../../data/mutations/apply-timeline";
import { GanttChart, GanttChartApi } from "./gantt-chart";
import { GanttTaskInput } from "./gantt-chart-types";
import { cn } from "../../../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";

export function GanttChartDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
    const { project } = useProject();
    const projectProgress = useProjectProgressQuery();
    const ganttRef = useRef<GanttChartApi>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);

    const applyTimelineMutation = useApplyTimelinesMutation({
        onSuccess: () => {
            onOpenChange(false);
            setHasUnsavedChanges(false);
        },
    });

    const tasks = useMemo((): GanttTaskInput[] => {
        return project.outline.workstreams.flatMap((ws, wsIndex) => {
            return ws.deliverables.flatMap((d, dIndex) => {
                if (!d.targetDate || !d.startDate) {
                    return [];
                }
                const progress = projectProgress.data?.getDeliverableProgress(ws.id, d.id) ?? 0;
                return {
                    id: `${ws.id}::${d.id}`,
                    name: `${String.fromCharCode(65 + wsIndex)}.${dIndex + 1} - ${d.name}`,
                    start: d.startDate,
                    end: d.targetDate,
                    progress: Math.round(progress * 100),
                    workstreamId: ws.id,
                    deliverableId: d.id,
                };
            });
        });
    }, [project.outline.workstreams, projectProgress.data]);

    const handleSaveChanges = () => {
        const updatedTasks = ganttRef.current?.getTasks();
        if (!updatedTasks) return;

        applyTimelineMutation.mutate({
            updates: updatedTasks.map((task) => ({
                workstreamId: task.workstreamId,
                deliverableId: task.deliverableId,
                startDate: DateTime.fromJSDate(task._start).toISODate()!,
                endDate: DateTime.fromJSDate(task._end).toISODate()!,
            })),
        });
    };

    const handleOpenChange = (isOpen: boolean) => {
        onOpenChange(isOpen);
        if (!isOpen) {
            setHasUnsavedChanges(false);
            setIsFullScreen(false);
        }
    };

    const ganttOptions = useMemo(
        () => ({
            readonly: false,
            on_date_change: () => setHasUnsavedChanges(true),
        }),
        []
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                onOpenAutoFocus={(e) => e.preventDefault()}
                className={cn(
                    isFullScreen && "h-screen max-h-screen w-screen max-w-none !rounded-none",
                    !isFullScreen && "max-w-3xl"
                )}
            >
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={() => setIsFullScreen(!isFullScreen)}
                            className="absolute right-12 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                            {isFullScreen ? (
                                <Minimize2Icon className="h-4 w-4" />
                            ) : (
                                <Maximize2Icon className="h-4 w-4" />
                            )}
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>{isFullScreen ? "Exit full screen" : "Full screen"}</TooltipContent>
                </Tooltip>
                <DialogTitle>Timeline</DialogTitle>
                <p className="flex flex-row items-center gap-2 text-sm text-muted-foreground">
                    You can update the project timeline by dragging or expanding deliverable task boxes.
                </p>
                {tasks.length > 0 && (
                    <div className={cn("relative", isFullScreen ? "h-[calc(100vh-200px)]" : "h-[500px]")}>
                        <GanttChart ref={ganttRef} tasks={tasks} ganttOptions={ganttOptions} />
                    </div>
                )}
                <DialogFooter>
                    <div className="flex flex-row gap-2">
                        <Button variant="outline" onClick={() => handleOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveChanges}
                            disabled={!hasUnsavedChanges || applyTimelineMutation.isPending}
                        >
                            {applyTimelineMutation.isPending && <Spinner className="me-2 h-4 w-4" />}
                            Save changes
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
